import {
    Card,
    Typography,
    Box,
    Stack,
    Chip,
    useTheme,
    Tooltip,
    useMediaQuery,
    CircularProgress,
    FormControl,
    MenuItem,
    Select,
} from "@mui/material";
import {rankColor} from "./helper";
import RegionDistributionTooltip from "./tooltips/regiondistributiontooltip";
import {useGetFacilityDistributionPerCity, useGetGeographicDistribution} from "../api/hooks";
import {GrowthIndicator} from "./common/growthindicator";
import DistributionIcons from "./common/distributionicons";
import AzureMap from "./common/azuremaps";
import {useState, useEffect} from "react";
import { DUMMY_AZURE_CITIES, DUMMY_GEOGRAPHIC } from "./walkthrough-dummy-data";

const GeographicDistributionCard = ({ isWalkthroughActive, walkthroughProductFilterOpen }: { isWalkthroughActive?: boolean; walkthroughProductFilterOpen?: boolean }) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("laptop"));

    const {data: realData = [], isLoading: realLoading} = useGetGeographicDistribution();
    const data = isWalkthroughActive ? DUMMY_GEOGRAPHIC : realData;
    const isLoading = isWalkthroughActive ? false : realLoading;

    const PRODUCT_FILTER_OPTIONS = [
        {label: "All Products", value: 1, color: "#22c55e"},
        {label: "Multiple Products", value: 2, color: "#ff5f47"},
        {label: "Health Information Hub", value: 3, color: "#123257"},
        {label: "Konsulta", value: 4, color: "#ffc300"},
        {label: "Beacon", value: 5, color: "#00d4ff"},
    ];

    const [cityFilter, setCityFilter] = useState<number>(1);
    const [selectOpen, setSelectOpen] = useState(false);

    // Keep the Select always in controlled-open mode so MUI v6 never has to
    // switch between uncontrolled (undefined) and controlled (true) — that
    // transition is unreliable and prevents the Portal from mounting.
    const isSelectOpen = walkthroughProductFilterOpen ? true : selectOpen;

    // When the walkthrough activates this step the paper Portal may mount a
    // frame after open=true arrives. Stamp the id on 60 ms / 200 ms retries
    // so the walkthrough's rect scan finds it even when the card is off-screen.
    useEffect(() => {
        if (!walkthroughProductFilterOpen) return;
        const stamp = () => {
            const paper = document.querySelector<HTMLElement>(".wt-product-filter-paper");
            if (paper) paper.id = "wt-product-filter-paper-id";
        };
        stamp();
        const t1 = setTimeout(stamp, 60);
        const t2 = setTimeout(stamp, 200);
        return () => { clearTimeout(t1); clearTimeout(t2); };
    }, [walkthroughProductFilterOpen]);

    const {data: realAzureData = [], isLoading: realAzureLoading} = useGetFacilityDistributionPerCity(cityFilter);
    const azureData = isWalkthroughActive ? DUMMY_AZURE_CITIES : realAzureData;
    const azureLoading = isWalkthroughActive ? false : realAzureLoading;

    return (
        <Card
            id="dashboard-geographic-card"
            sx={{
                p: 3,
                borderRadius: 3,
                backgroundColor: "#eef3ff",
                minHeight: 520,
                boxShadow: "0px 4px 6px rgba(15, 23, 42, 0.18)",
            }}
        >
            <Typography fontWeight={600} fontSize={20} paddingInline={3} textAlign="left" color="primary.main">
                Geographic Distribution Dashboard
            </Typography>

            <Stack direction="row" spacing={2} height={460}>
                {azureLoading ? (
                    <Box
                        sx={{
                            flex: "0 0 35%",
                            height: "450px",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 1.5,
                        }}
                    >
                        <CircularProgress size={36} thickness={4} />

                        <Typography fontSize={14} fontWeight={600} color="text.secondary">
                            Fetching Data...
                        </Typography>
                    </Box>
                ) : (
                    <Box
                        sx={{
                            flex: "0 0 35%",
                            minWidth: 430,
                            maxWidth: 430,
                            borderRadius: 3,
                            p: 2,
                            position: "relative",
                        }}
                    >
                        {/* ===== FLOATING FILTER (CENTERED) ===== */}
                        <Box
                            sx={{
                                position: "absolute",
                                top: 5,
                                left: "50%",
                                transform: "translateX(-50%)",
                                zIndex: 10,
                                minWidth: 400,
                            }}
                        >
                            <FormControl id="dashboard-product-filter" size="small" fullWidth>
                                <Select
                                    value={cityFilter}
                                    open={isSelectOpen}
                                    onOpen={walkthroughProductFilterOpen ? () => {} : () => setSelectOpen(true)}
                                    onClose={walkthroughProductFilterOpen ? () => {} : () => setSelectOpen(false)}
                                    MenuProps={{ classes: { paper: "wt-product-filter-paper" } }}
                                    displayEmpty
                                    renderValue={(value) => {
                                        const option = PRODUCT_FILTER_OPTIONS.find((o) => o.value === value);

                                        return (
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <Box
                                                    sx={{
                                                        width: 10,
                                                        height: 10,
                                                        borderRadius: "50%",
                                                        backgroundColor: option?.color ?? "#22c55e",
                                                        border: "2px solid #ffffff",
                                                        boxShadow: "0 2px 3px rgba(0,0,0,0.35)",
                                                    }}
                                                />
                                                <Typography fontSize={14} color={"primary.main"}>
                                                    {option?.label ?? "All Products"}
                                                </Typography>
                                            </Stack>
                                        );
                                    }}
                                    onChange={(e) => {
                                        setCityFilter(Number(e.target.value));
                                        setSelectOpen(false);
                                    }}
                                    sx={{
                                        backgroundColor: "#ffffff",
                                        borderRadius: 2,
                                        px: 1,
                                        boxShadow: "0 6px 18px rgba(15,23,42,0.25)",

                                        "& .MuiOutlinedInput-notchedOutline": {
                                            border: "none",
                                        },
                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                            border: "none",
                                        },
                                        "&:hover .MuiOutlinedInput-notchedOutline": {
                                            border: "none",
                                        },
                                    }}
                                >
                                    {PRODUCT_FILTER_OPTIONS.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <Box
                                                    sx={{
                                                        width: 14,
                                                        height: 14,
                                                        borderRadius: "50%",
                                                        backgroundColor: option.color,
                                                        border: "2px solid #ffffff",
                                                        boxShadow: "0 2px 3px rgba(0,0,0,0.35)",
                                                    }}
                                                />

                                                <Typography fontSize={14} color={"primary.main"}>
                                                    {option.label}
                                                </Typography>
                                            </Stack>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        <Box id="dashboard-map" sx={{height: 450}}>
                            <AzureMap cities={azureData} isLoading={azureLoading} />
                        </Box>
                    </Box>
                )}

                <Box
                    sx={{
                        flex: 1.9,
                        borderRadius: 3,
                        p: 2,
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <Typography fontWeight={600} mb={1.5} color="primary.main">
                        Top 10 Regions
                    </Typography>

                    <Stack
                        direction="row"
                        sx={{
                            px: 1,
                            pb: 1,
                            fontSize: 14,
                            fontWeight: 600,
                            color: "primary.main",
                            borderBottom: `1px solid  ${theme.palette.primary.main}`,
                        }}
                    >
                        <Box width={44} />
                        <Box flex={1}>Region</Box>
                        <Box width={150} textAlign="center">
                            Total Facilities
                        </Box>
                        <Box width={100} textAlign="center">
                            Top Product
                        </Box>
                        <Box width={180} textAlign="center">
                            Product Distribution
                        </Box>
                    </Stack>

                    {/* ROWS */}

                    <Box
                        id="dashboard-top-regions"
                        sx={{
                            flex: 1,
                            overflowY: "auto",

                            "&::-webkit-scrollbar": {
                                width: 10,
                            },
                            "&::-webkit-scrollbar-track": {
                                background: "#eef3ff",
                                borderRadius: 8,
                            },
                            "&::-webkit-scrollbar-thumb": {
                                backgroundColor: "rgba(15,23,42,0.45)",
                                borderRadius: 8,
                            },
                            "&::-webkit-scrollbar-thumb:hover": {
                                backgroundColor: "rgba(15,23,42,0.65)",
                            },

                            scrollbarColor: "rgba(15,23,42,0.45) #eef3ff",
                        }}
                    >
                        {isLoading ? (
                            <Box
                                sx={{
                                    height: "350px",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 1.5,
                                }}
                            >
                                <CircularProgress size={36} thickness={4} />

                                <Typography fontSize={14} fontWeight={600} color="text.secondary">
                                    Fetching Data...
                                </Typography>
                            </Box>
                        ) : data.length === 0 ? (
                            <Box
                                sx={{
                                    height: "350px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <Box
                                    sx={{
                                        textAlign: "center",
                                        color: "#6D87AA",
                                        fontSize: "18px",
                                        fontWeight: 500,
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        gap: 1,
                                    }}
                                >
                                    <img src="/empty-table-data-logo.svg" alt="No Data" style={{maxHeight: 300}} />
                                    <Box
                                        component="span"
                                        sx={{
                                            fontFamily: "Poppins",
                                            fontWeight: 600,
                                            fontSize: "24px",
                                            color: "#A1A1A1",
                                            textAlign: "center",
                                        }}
                                    >
                                        {"No Data Found"}
                                    </Box>
                                </Box>
                            </Box>
                        ) : (
                            <>
                                {data.map((region) => (
                                    <Stack
                                        key={region.rank}
                                        sx={{
                                            backgroundColor: region.rank % 2 === 1 ? "#eef3ff" : "#f6f6fd",
                                            px: 1,
                                            py: 0.5,
                                        }}
                                    >
                                        <Tooltip
                                            disableInteractive
                                            placement={isSmallScreen ? "bottom" : "left"}
                                            arrow
                                            title={
                                                <RegionDistributionTooltip
                                                    region={{
                                                        rank: region.rank,
                                                        name: region.regionName,
                                                        facilities: region.facilities,
                                                        topProduct: region.topProduct,
                                                        growthPercentage: region.growthPercentage ?? 0,
                                                        distribution: region.distribution,
                                                    }}
                                                />
                                            }
                                            componentsProps={{
                                                tooltip: {
                                                    sx: {
                                                        backgroundColor: "transparent",
                                                        boxShadow: "none",
                                                        padding: 0,
                                                    },
                                                },
                                                arrow: {
                                                    sx: {
                                                        color: "#020617",
                                                    },
                                                },
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    width: "100%",
                                                    px: 1,
                                                    py: 1.3,
                                                    borderRadius: 4,
                                                    cursor: "pointer",
                                                    transition:
                                                        "transform 0.2s ease, box-shadow 0.2s ease, background-color 0.15s ease",
                                                    "&:hover": {
                                                        backgroundColor: "#eaf0ff",
                                                        boxShadow: `inset 0 0 0 1px #6365f14a,0 2px 2px rgba(15, 23, 42, 0.18)`,
                                                        transform: "translateY(-1px)",
                                                    },
                                                    "&:hover .product-chip": {
                                                        backgroundColor: "#3b82f6",
                                                        color: "#ffffff",
                                                        boxShadow: "0 4px 10px rgba(59,130,246,0.45)",
                                                        transform: "scale(1.05)",
                                                    },
                                                    "&:hover .rank-badge": {
                                                        transform: "scale(1.10)",
                                                    },
                                                    "&:hover .trend-icon": {
                                                        transform: "translateY(-2px)",
                                                        color: "#66b483",
                                                    },
                                                }}
                                            >
                                                <Box
                                                    className="rank-badge"
                                                    sx={{
                                                        width: 30,
                                                        height: 30,
                                                        borderRadius: "50%",
                                                        background: rankColor(region.rank),
                                                        color: region.rank <= 2 ? "#000000" : "#fff",
                                                        fontWeight: 700,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        transition: "transform 0.2s ease, box-shadow 0.2s ease",
                                                    }}
                                                >
                                                    {region.rank}
                                                </Box>

                                                <Stack direction="row" spacing={1} alignItems="center" flex={1} ml={2}>
                                                    <Typography
                                                        fontWeight={600}
                                                        fontSize={14}
                                                        noWrap
                                                        sx={{color: "primary.main"}}
                                                    >
                                                        {region.regionName}
                                                    </Typography>
                                                    <GrowthIndicator value={region.growthPercentage ?? 0} />
                                                </Stack>

                                                <Box
                                                    width={120}
                                                    textAlign="center"
                                                    fontWeight={700}
                                                    color="primary.main"
                                                >
                                                    {region.facilities}
                                                </Box>

                                                <Box width={130} textAlign="center">
                                                    <Chip
                                                        label={region.topProduct}
                                                        size="small"
                                                        className="product-chip"
                                                        sx={{
                                                            backgroundColor: "#e0f2fe",
                                                            color: "#0f69aa",
                                                            transition: "all 0.2s ease",
                                                        }}
                                                    />
                                                </Box>
                                                <Box width={140} display="flex" justifyContent="center">
                                                    <DistributionIcons items={region.distribution} />
                                                </Box>
                                            </Box>
                                        </Tooltip>
                                    </Stack>
                                ))}
                            </>
                        )}
                    </Box>
                </Box>
            </Stack>
        </Card>
    );
};

export default GeographicDistributionCard;
